import { useEffect } from 'react';
import RadioButton from '../atoms/RadioButton';
import { PaymentProvider, PaymentType } from '../../models/payment';
import PayWayCheckout from '../PayWayCheckout';
import NaveCheckout, { NaveCheckoutResult } from '../NaveCheckout';
import { PayWayTokenData } from '../../store/apis/orders';

interface PaymentProviderConfig {
  provider: PaymentProvider;
  label: string;
  description: string;
  enabled: boolean;
}

export const shouldShowSubmitButton = (
  paymentProvider: PaymentProvider | null,
  paymentMethod: PaymentType | null,
  totalToPay: number | undefined,
  isBackoffice: boolean,
  navePaymentRequestId: string | null,
): boolean => {
  const showPayWayCheckout = !!(paymentProvider === PaymentProvider.PAYWAY
    && paymentMethod === PaymentType.ONLINE
    && totalToPay
    && totalToPay > 0
    && !isBackoffice);

  const showNaveCheckout = !!(paymentProvider === PaymentProvider.NAVE
    && paymentMethod === PaymentType.ONLINE
    && totalToPay
    && totalToPay > 0
    && !isBackoffice
    && navePaymentRequestId);

  return !showPayWayCheckout && !showNaveCheckout;
};

interface CheckoutPaymentOptionsProps {
  paymentProvider: PaymentProvider | null;
  paymentMethod: PaymentType | null;
  showManualOption: boolean;
  allowMercadoPago: boolean;
  allowUala: boolean;
  allowPayWay: boolean;
  allowNave: boolean;
  allowNaveLink: boolean;
  allowZilo: boolean;
  disabled?: boolean;
  onProviderSelect: (provider: PaymentProvider | null, method: PaymentType | null) => void;
  // Props for embedded checkout forms
  totalToPay?: number;
  isBackoffice?: boolean;
  clickedPay?: boolean;
  isPayWayProcessing?: boolean;
  setIsPayWayProcessing?: (processing: boolean) => void;
  handlePayWayTokenCreated?: (tokenData: PayWayTokenData) => void;
  handlePayWayError?: (error: string) => void;
  orderItemData?: Array<{
    name: string;
    last_name: string;
    person_id: string;
  }>;
  navePaymentRequestId?: string | null;
  isNaveProcessing?: boolean;
  setIsNaveProcessing?: (processing: boolean) => void;
  handleNavePaymentComplete?: (result: NaveCheckoutResult) => void;
  handleNaveError?: (error: string) => void;
}

/**
 * CheckoutPaymentOptions - Payment method/provider selection
 *
 * Theme Support: ✅ Full light/dark mode support
 * Colors Used: text-text-secondary for descriptions
 */
const CheckoutPaymentOptions: React.FC<CheckoutPaymentOptionsProps> = ({
  paymentProvider,
  paymentMethod,
  showManualOption,
  allowMercadoPago,
  allowUala,
  allowPayWay,
  allowNave,
  allowNaveLink,
  allowZilo,
  disabled = false,
  onProviderSelect,
  totalToPay,
  isBackoffice = false,
  clickedPay = false,
  isPayWayProcessing = false,
  setIsPayWayProcessing,
  handlePayWayTokenCreated,
  handlePayWayError,
  orderItemData,
  navePaymentRequestId,
  isNaveProcessing = false,
  setIsNaveProcessing,
  handleNavePaymentComplete,
  handleNaveError,
}) => {
  const paymentProviders: PaymentProviderConfig[] = [
    {
      provider: PaymentProvider.MERCADOPAGO,
      label: 'Mercado Pago',
      description: 'Paga con tu cuenta de Mercado Pago o tarjetas asociadas.',
      enabled: allowMercadoPago,
    },
    {
      provider: PaymentProvider.UALA,
      label: 'Ualá',
      description: 'Paga con tu billetera Ualá.',
      enabled: allowUala,
    },
    {
      provider: PaymentProvider.PAYWAY,
      label: 'PayWay (Tarjeta)',
      description: 'Paga directamente con tu tarjeta de crédito o débito.',
      enabled: allowPayWay,
    },
    {
      provider: PaymentProvider.NAVE,
      label: 'Nave (Naranja X)',
      description: 'Paga con Naranja X o tarjetas asociadas.',
      enabled: false,
    },
    {
      provider: PaymentProvider.NAVE_LINK,
      label: showManualOption ? 'Tarjeta de crédito / débito / app bancaria' : 'Tarjeta de crédito / débito / app bancaria',
      description: 'Serás redirigido para completar el pago.',
      enabled: allowNave && allowNaveLink,
    },
    {
      provider: PaymentProvider.ZILO,
      label: 'Transferencia',
      description: 'Transferí a un CVU y recibí tus entradas en instantes.',
      enabled: allowZilo,
    },
  ];

  const handleProviderSelect = (provider: PaymentProvider, method: PaymentType) => {
    onProviderSelect(provider, method);
  };

  const handleManualSelect = () => {
    onProviderSelect(null, PaymentType.MANUAL);
  };

  // Auto-select if there's only one payment option available
  useEffect(() => {
    // Only auto-select if nothing is currently selected
    if (paymentProvider !== null || paymentMethod !== null) {
      return;
    }

    const enabledProviders = paymentProviders.filter((p) => p.enabled);
    const totalOptions = enabledProviders.length + (showManualOption ? 1 : 0);

    // If there's exactly one option, auto-select it
    if (totalOptions === 1) {
      if (showManualOption && enabledProviders.length === 0) {
        // Only manual option available
        handleManualSelect();
      } else if (enabledProviders.length === 1) {
        // Only one provider available
        handleProviderSelect(enabledProviders[0].provider, PaymentType.ONLINE);
      }
    }
  }, [
    paymentProvider,
    paymentMethod,
    allowMercadoPago,
    allowUala,
    allowPayWay,
    allowNave,
    allowNaveLink,
    allowZilo,
    showManualOption,
  ]);

  const title = showManualOption ? 'Método de pago' : 'Medio de pago';
  const descriptionSize = showManualOption ? 'text-xs' : 'text-sm';

  // Computed values for conditional rendering
  const showPayWayCheckout = !!(paymentProvider === PaymentProvider.PAYWAY
    && paymentMethod === PaymentType.ONLINE
    && totalToPay
    && totalToPay > 0
    && !isBackoffice);

  const showNaveCheckout = !!(paymentProvider === PaymentProvider.NAVE
    && paymentMethod === PaymentType.ONLINE
    && totalToPay
    && totalToPay > 0
    && !isBackoffice
    && navePaymentRequestId);

  return (
    <>
      <div className="glass-surface rounded-3xl flex flex-col gap-4 p-3 self-stretch">
        <h1 className="text-lg font-semibold">{title}</h1>
        <div className="flex flex-col gap-3">
          {paymentProviders
            .filter((p) => p.enabled)
            .map((providerConfig) => (
              <div className="flex flex-col" key={providerConfig.provider}>
                <RadioButton
                  name="paymentProvider"
                  value={providerConfig.provider}
                  checked={paymentProvider === providerConfig.provider}
                  label={providerConfig.label}
                  disabled={disabled}
                  onChange={() => handleProviderSelect(providerConfig.provider, PaymentType.ONLINE)}
                />
                <span className={`${descriptionSize} text-text-secondary ml-7 ${!showManualOption ? 'mt-1' : ''}`}>
                  {providerConfig.description}
                </span>
              </div>
            ))}
          {showManualOption && (
            <div className="flex flex-col">
              <RadioButton
                name="paymentMethod"
                value="manual"
                checked={paymentMethod === PaymentType.MANUAL}
                label="Transferencia"
                disabled={disabled}
                onChange={handleManualSelect}
              />
              <span className="text-xs text-text-secondary ml-7">
                Enviarás un mensaje por WhatsApp a una operadora que te indicará como realizar el pago.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* PayWay Credit Card Form */}
      {showPayWayCheckout && handlePayWayTokenCreated && handlePayWayError && setIsPayWayProcessing && orderItemData && (
        <PayWayCheckout
          amount={Math.round(totalToPay * 100)}
          onTokenCreated={handlePayWayTokenCreated}
          onError={handlePayWayError}
          onProcessing={setIsPayWayProcessing}
          disabled={clickedPay || isPayWayProcessing}
          buttonText="Pagar con tarjeta"
          orderItems={orderItemData.map((item) => ({
            name: item.name,
            last_name: item.last_name,
            person_id: item.person_id,
          }))}
        />
      )}

      {/* Nave Checkout Form */}
      {showNaveCheckout && handleNavePaymentComplete && handleNaveError && setIsNaveProcessing && (
        <NaveCheckout
          paymentRequestId={navePaymentRequestId}
          onPaymentComplete={handleNavePaymentComplete}
          onError={handleNaveError}
          onProcessing={setIsNaveProcessing}
          disabled={clickedPay || isNaveProcessing}
        />
      )}
    </>
  );
};

export default CheckoutPaymentOptions;
